import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, memo, useContext, useMemo, useState } from 'react';
import { UserRole } from '@/services/ws-permissions/src/main/resources/rest-api/index.ts';
import { PermissionType } from '@/js/libs/generated/permissions/types.ts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { AGENCY_ROLES, NEW_COMPANY_EXCLUDED_PERMISSIONS_SET, YALEO_SELF_BOOKING_USER_ROLES } from './constants.js';
import { useImpersonatedUserStorage } from './hooks/useImpersonatedUserStorage.js';
import { useSelectedCompanyStorage } from './hooks/useSelectedCompanyStorage.js';
import useTokenStorage from './hooks/useTokenStorage.js';
import { CombinedRole } from './types.js';
const AuthContext = createContext(null);
export const AuthProvider = memo(function AuthProvider({ children }) {
    const { getAuthToken } = useTokenStorage();
    const { getSelectedCompany, getTemporaryAccessExpiresAt } = useSelectedCompanyStorage();
    const { getImpersonatedUser } = useImpersonatedUserStorage();
    const [token, setToken] = useState(getAuthToken);
    const [identity, setIdentity] = useState();
    const [loggedUser, setLoggedUser] = useState();
    const [impersonatedUser, setImpersonatedUser] = useState(getImpersonatedUser);
    const [companySelection, setCompanySelection] = useState(() => ({
        selectedCompany: getSelectedCompany(),
        temporaryAccessExpiresAt: getTemporaryAccessExpiresAt(),
    }));
    const userRole = identity?.userRole;
    const permissions = identity?.permissions;
    // Built from the role and the permissions alone: an identity updated in its other fields, such as a saved profile,
    // keeps its roles, and whatever depends on them stays as it is.
    const identityRoles = useMemo(() => {
        if (!permissions) {
            return [PermissionType.ANONYMOUS];
        }
        const isAdmin = userRole === UserRole.ADMIN;
        const isAgencyRelatedRoles = isAdmin || AGENCY_ROLES.some((role) => permissions.includes(role));
        const isSelfBookingUser = permissions.some((permission) => permission === PermissionType.YALEO_SELF_BOOKING) &&
            permissions.every((permission) => YALEO_SELF_BOOKING_USER_ROLES.includes(permission));
        const isNewCompany = permissions.every((permission) => NEW_COMPANY_EXCLUDED_PERMISSIONS_SET.has(permission));
        return [
            userRole,
            ...permissions,
            ...(isAdmin && !permissions.includes(PermissionType.PUBLISHER) ? [PermissionType.PUBLISHER] : []),
            ...(isAgencyRelatedRoles ? [CombinedRole.AGENCY] : []),
            ...(isSelfBookingUser ? [CombinedRole.YALEO_SELF_BOOKING_USER] : []),
            ...(isNewCompany ? [CombinedRole.NEW_COMPANY] : []),
        ].filter((role) => !!role);
    }, [permissions, userRole]);
    const setSelectedCompany = useCallbackRef((selectedCompany, temporaryAccessExpiresAt = null) => setCompanySelection({ selectedCompany, temporaryAccessExpiresAt }));
    const value = useMemo(() => ({
        identity,
        setIdentity,
        identityRoles,
        loggedUser,
        setLoggedUser,
        impersonatedUser,
        setImpersonatedUser,
        selectedCompany: companySelection.selectedCompany,
        temporaryAccessExpiresAt: companySelection.temporaryAccessExpiresAt,
        setSelectedCompany,
        token,
        setToken,
    }), [companySelection, identity, identityRoles, impersonatedUser, loggedUser, setSelectedCompany, token]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
});
export const useAuth = () => {
    const value = useContext(AuthContext);
    if (!value) {
        throw new Error('`useAuth` is used outside of `AuthProvider`');
    }
    return value;
};
